// Phone number gate for /me/:agentId.
// First screen the user sees. Submits phone → POST verify → on success
// calls onVerified(session, phone) so the parent can use the phone for name fallback.
// Shows lockout message / remaining attempts on failure.

import { useCallback, useEffect, useRef, useState } from 'react'
import { verifyDashboardPhone } from '../lib/api'
import type { Strings } from '../lib/i18n'

interface Props {
  agentId: string
  strings: Strings
  onVerified: (session: string, phone: string) => void
  /**
   * Phone from a `?phone=` URL param (operator links from an internal admin tool).
   * When set, the input is pre-filled and verify is auto-triggered once. On failure
   * we fall back to the normal manual gate (pre-filled value kept) — no auto-retry loop.
   */
  autoPhone?: string | null
}

type Status = 'idle' | 'loading' | 'error'

export function PhoneGate({ agentId, strings: s, onVerified, autoPhone }: Props) {
  // Pre-fill from the operator param so the input shows the value even if auto-verify fails.
  const [phone, setPhone] = useState(autoPhone ?? '')
  // Show the verifying state immediately when auto-verify will fire, so the operator
  // never sees a flash of the empty/idle gate before the request kicks off.
  const [status, setStatus] = useState<Status>(autoPhone ? 'loading' : 'idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // The shared verify path — used by both the manual form submit and the auto-trigger.
  // Returns nothing; updates status/error and calls onVerified on success.
  const verify = useCallback(async (rawPhone: string) => {
    const trimmed = rawPhone.trim()
    if (!trimmed) return
    setStatus('loading')
    setErrorMsg(null)

    try {
      const result = await verifyDashboardPhone(agentId, trimmed)
      if (result.ok) {
        try {
          sessionStorage.setItem(`dashboard_session_${agentId}`, result.session)
        } catch { /* sessionStorage blocked — pass session in-memory */ }
        onVerified(result.session, trimmed)
      } else {
        const extra = result.remaining !== undefined ? ' ' + s.gateRemainingAttempts(result.remaining) : ''
        setErrorMsg((result.message ?? s.gateVerifyFailed) + extra)
        setStatus('error')
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : s.loadError)
      setStatus('error')
    }
  }, [agentId, onVerified, s])

  // Auto-trigger verify once when an operator opened the link with `?phone=`.
  // Guarded by a ref so a re-render (or auto-verify failure → 'error') never re-fires it.
  const autoFired = useRef(false)
  useEffect(() => {
    if (autoFired.current) return
    if (!autoPhone || !autoPhone.trim()) return
    autoFired.current = true
    void verify(autoPhone)
  }, [autoPhone, verify])

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void verify(phone)
  }

  return (
    <div className="phone-gate">
      <div className="phone-gate-card">
        <div className="phone-gate-brand">{s.brand}</div>
        <h1 className="phone-gate-title">{s.gateTitle}</h1>
        <p className="phone-gate-desc">{s.gateDesc}</p>

        <form onSubmit={onSubmit} className="phone-gate-form">
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder={s.gatePlaceholder}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={status === 'loading'}
            className="phone-gate-input"
            required
          />
          <button
            type="submit"
            className="phone-gate-btn"
            disabled={status === 'loading' || !phone.trim()}
          >
            {status === 'loading' ? s.gateSubmitting : s.gateSubmit}
          </button>
        </form>

        {errorMsg && (
          <p className="phone-gate-error" role="alert">
            {errorMsg}
          </p>
        )}
      </div>
    </div>
  )
}
